'use client';

import React, { useState } from "react";
import { Loader2, UserPlus, Copy, Check, PartyPopper } from "lucide-react";
import { Drawer } from "./Drawer";
import { RolePicker } from "./RolePicker";
import { useTeam, errText, inviteJoinMessage, inviteWhatsAppUrl } from "../internals";
import { useToast } from "./Toaster";

const onlyDigits = (s) => String(s || "").replace(/\D/g, "").slice(0, 10);

export const InviteDrawer = ({ open, onClose, onInvited }) => {
  const { storage, currentUser, costume } = useTeam();
  const toast = useToast();

  const isManager = currentUser.role === "manager";
  const allRoles = costume.visibleRoles || ["owner", "admin", "manager", "staff"];
  // Owner's product decision (8 Aug 2026): one senior on top, staff beneath.
  // Every invite joins as Staff — no admin/manager tiers offered.
  const roles = ["staff"];

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [designation, setDesignation] = useState("");
  const [role, setRole] = useState("staff");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(null); // {name, phone} after a saved invite
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setName("");
    setPhone("");
    setDesignation("");
    setRole("staff");
    setErr(null);
    setSent(null);
    setCopied(false);
  };

  const close = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const valid = name.trim().length > 0 && phone.length === 10;

  const submit = async () => {
    setErr(null);
    if (name.trim().length === 0) return setErr("Please type their name.");
    if (phone.length !== 10) return setErr("Please enter a 10-digit mobile number.");
    setSubmitting(true);
    try {
      await storage.createInvite({
        name: name.trim(),
        phone,
        role,
        designation: designation.trim() || null,
      });
      toast.show(`${name.trim()} added to Pending.`, "success");
      setSent({ name: name.trim(), phone });
      onInvited && onInvited();
    } catch (e) {
      const m = errText(e);
      setErr(m);
      toast.show(m, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const copySteps = async () => {
    try {
      await navigator.clipboard.writeText(inviteJoinMessage(sent));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — WhatsApp button still works */ }
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      testId="invite-drawer"
      title={sent ? "One more step" : "Invite a team member"}
      subtitle={sent ? `Send ${sent.name} the joining steps — Jigyasa doesn't message them automatically.` : "They join from the login screen by tapping 'Join your team' and entering this number. Valid for 7 days."}
      footer={
        sent ? (
          <button
            type="button"
            data-testid="invite-done-btn"
            onClick={close}
            className="w-full rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Done
          </button>
        ) : (
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="invite-cancel-btn"
            onClick={close}
            disabled={submitting}
            className="flex-1 rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="invite-submit-btn"
            onClick={submit}
            disabled={submitting || !valid}
            className="flex flex-1 items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
          >
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
            Save invite
          </button>
        </div>
        )
      }
    >
      {sent ? (
        <div data-testid="invite-sent-panel" className="space-y-4">
          <div className="flex items-start gap-3 rounded-md bg-green-50 px-3 py-3 text-sm text-green-800">
            <PartyPopper size={18} className="mt-0.5 shrink-0" />
            <span><strong>{sent.name}</strong> is added to Pending. Now tell them how to join — one tap:</span>
          </div>
          <a
            data-testid="invite-sent-whatsapp"
            href={inviteWhatsAppUrl(sent)}
            target="_blank"
            rel="noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-3 text-sm font-medium text-white hover:bg-green-700"
          >
            Send joining steps on WhatsApp
          </a>
          <button
            type="button"
            data-testid="invite-sent-copy"
            onClick={copySteps}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-zinc-300 px-4 py-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
          >
            {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
            {copied ? "Copied ✓" : "Copy the steps instead"}
          </button>
          <p className="text-xs text-zinc-400">
            No rush — these buttons also stay on their row under the Pending tab.
          </p>
        </div>
      ) : (
      <div className="space-y-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Their name</label>
          <input
            data-testid="invite-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="e.g. Sneha Sharma"
            className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-zinc-900"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">Mobile number</label>
          <div className="flex items-center rounded-md border border-zinc-300 focus-within:border-zinc-900">
            <span className="px-3 text-sm text-zinc-500">+91</span>
            <input
              data-testid="invite-phone-input"
              value={phone}
              onChange={(e) => setPhone(onlyDigits(e.target.value))}
              inputMode="numeric"
              placeholder="10-digit number"
              className="w-full rounded-r-md px-1 py-2.5 text-sm outline-none"
            />
          </div>
          <p className="mt-1 text-xs text-zinc-400">{phone.length}/10 digits</p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">
            Job title <span className="font-normal text-zinc-400">(optional)</span>
          </label>
          <input
            data-testid="invite-designation-input"
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
            placeholder="e.g. Sales executive"
            className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-zinc-900"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-zinc-700">What can they do?</label>
          {isManager && (
            <p className="mb-2 text-xs text-zinc-400">As a manager, you can invite staff members.</p>
          )}
          <RolePicker roles={roles} value={role} onChange={setRole} disabled={isManager} />
        </div>

        {err && (
          <div data-testid="invite-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {err}
          </div>
        )}
      </div>
      )}
    </Drawer>
  );
};
